import type { RegimeDependenceProfileReportRecord } from '@/types/appRouter';
import { FaLock } from 'react-icons/fa';
import { Card, Disclosure, StatusBadge, Tooltip } from '@/components/UI';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import { RegimeRelativeEdgeChip } from './RegimeRelativeEdgeChip';
import {
    formatInt,
    getPrimaryDimension,
    getTotalPrimaryEpisodes,
    getVerdictPresentation,
    type RegimeVerdictTone
} from './regimeProfilePresentation';

export const VERDICT_BADGE_TONES: Record<RegimeVerdictTone, 'success' | 'warning' | 'danger' | 'neutral'> = {
    danger: 'danger',
    neutral: 'neutral',
    success: 'success',
    warning: 'warning'
};

function PopulationExclusionTooltip({ record }: { record: RegimeDependenceProfileReportRecord }) {
    const { population } = record.report;

    const lines = [
        `${formatInt(population.candidateCount)} sealed-window candidates`,
        `${formatInt(population.excludedOutcomeCrossesTrainingEndCount)} excluded — outcome window crosses the training end (structurally unprofilable inside the sealed window)`,
        `${formatInt(population.excludedMissingOutcomeCount)} excluded — missing outcome`,
        `${formatInt(population.excludedMissingResultCount)} excluded — no result row at this horizon`,
        `${formatInt(population.excludedNotExecutedCount)} excluded — not executed`
    ];

    return (
        <Tooltip content={lines.join('\n')}>
            <span className="cursor-help underline decoration-dotted decoration-zinc-600 underline-offset-2">
                {formatInt(population.includedTradeCount)} trades
            </span>
        </Tooltip>
    );
}

function LineageDisclosure({ record }: { record: RegimeDependenceProfileReportRecord }) {
    const { lineage } = record.report;
    const scopes = [
        { key: 'market' as const, label: 'Market' },
        { key: 'entity' as const, label: 'Entity' },
        { key: 'relative' as const, label: 'Relative' },
        { key: 'oneilDirection' as const, label: "O'Neil direction" }
    ];

    return (
        <Disclosure
            title={
                <span className="text-xs text-zinc-500">
                    Model lineage: market {lineage.resolvedModelVersionIds.market} · entity {lineage.resolvedModelVersionIds.entity} ·
                    relative {lineage.resolvedModelVersionIds.relative} · O&apos;Neil {lineage.resolvedModelVersionIds.oneilDirection}
                </span>
            }
            variant="plain"
        >
            <div className="grid gap-4 text-xs md:grid-cols-2">
                <table className="w-full text-left">
                    <thead>
                        <tr className="text-zinc-500">
                            <th className="py-1 pr-3 font-medium">Scope</th>
                            <th className="py-1 pr-3 font-medium">Version</th>
                            <th className="py-1 pr-3 font-medium">Key</th>
                            <th className="py-1 font-medium">Source</th>
                        </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                        {scopes.map((scope) => (
                            <tr key={scope.key}>
                                <td className="py-1 pr-3 text-zinc-400">{scope.label}</td>
                                <td className="py-1 pr-3">{lineage.resolvedModelVersionIds[scope.key]}</td>
                                <td className="break-all py-1 pr-3 font-mono">{lineage.resolvedModelVersionKeys[scope.key]}</td>
                                <td className="py-1">{lineage.modelVersionSources[scope.key]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="space-y-1 text-zinc-400">
                    <div>
                        Market key <span className="font-mono text-zinc-300">{lineage.marketKey}</span> · benchmark{' '}
                        <span className="font-mono text-zinc-300">{lineage.relativeBenchmarkKey}</span>
                    </div>
                    <div>
                        O&apos;Neil system <span className="font-mono text-zinc-300">{lineage.oneilSystemKey}</span> · regime horizon{' '}
                        {lineage.regimeHorizon}
                    </div>
                    <div>
                        {lineage.entityType === 'Asset'
                            ? 'Stocks'
                            : lineage.entityType === 'AssetIndex'
                              ? 'Indexes'
                              : lineage.entityType === 'mixed'
                                ? 'Mixed entities'
                                : 'Cryptocurrency'}{' '}
                        · duration {lineage.duration} · report v{record.reportVersion} · report #{record.id}
                        {record.jobId !== null && ` · run #${record.jobId}`}
                    </div>
                    <div>Profiled {record.createdAt}</div>
                    {lineage.strategyRegimeModelVersionOverrides !== null && (
                        <div>
                            Strategy pins at profile time:{' '}
                            <span className="font-mono text-zinc-300">{JSON.stringify(lineage.strategyRegimeModelVersionOverrides)}</span>
                        </div>
                    )}
                    {lineage.realizedExitLineage !== null && (
                        <div>
                            Realized exit: version {lineage.realizedExitLineage.tradeLogicV2ExitVersionId} · fingerprint{' '}
                            <span className="font-mono text-zinc-300">{lineage.realizedExitLineage.exitFingerprint ?? 'none'}</span> ·
                            candidate #{lineage.realizedExitLineage.exitCandidateId} (expected from{' '}
                            {lineage.realizedExitLineage.expectedSource})
                        </div>
                    )}
                </div>
            </div>
        </Disclosure>
    );
}

interface RegimeVerdictHeaderProps {
    record: RegimeDependenceProfileReportRecord;
}

/**
 * Layer 1 — the verdict header. The verdict badge and the relative-edge chip render as one unit and are
 * never shown apart (design rule 2); the sealed-window marker is permanent chrome (design rule 5).
 */
export function RegimeVerdictHeader({ record }: RegimeVerdictHeaderProps) {
    const { report } = record;
    const presentation = getVerdictPresentation(report.verdict);
    const primary = getPrimaryDimension(report);
    const totalEpisodes = getTotalPrimaryEpisodes(primary);
    const { population, lineage } = report;

    const basisParts =
        report.input.outcomeMode === 'fixedHorizon'
            ? [
                  'Fixed horizon',
                  `${report.input.outcomeBasis ?? 'underlying'} basis`,
                  ...(report.input.horizonSeconds !== null ? [formatTimeHorizon(report.input.horizonSeconds)] : [])
              ]
            : [
                  'Realized strategy exit',
                  ...(lineage.realizedInstrumentBasis !== null ? [`${lineage.realizedInstrumentBasis} instruments`] : []),
                  ...(report.input.realizedOutcomeSetId !== null ? [`outcome set #${report.input.realizedOutcomeSetId}`] : [])
              ];

    return (
        <Card className="bg-zinc-900 border border-zinc-800">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <StatusBadge
                        className="uppercase tracking-wide"
                        status={report.verdict.verdict}
                        tone={VERDICT_BADGE_TONES[presentation.tone]}
                    >
                        {presentation.label}
                    </StatusBadge>
                    <span className="text-sm text-zinc-300">{presentation.gloss}</span>
                </div>
                <RegimeRelativeEdgeChip report={record.report} />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-zinc-400">
                <PopulationExclusionTooltip record={record} />
                <span>·</span>
                <span>{formatInt(population.distinctEntityCount)} entities</span>
                <span>·</span>
                <span>{formatInt(totalEpisodes)} episodes</span>
                {population.firstEntrySessionDate !== null && population.lastEntrySessionDate !== null && (
                    <>
                        <span>·</span>
                        <span>
                            {population.firstEntrySessionDate} → {population.lastEntrySessionDate}
                        </span>
                    </>
                )}
            </div>

            <div className="mt-1 text-sm text-zinc-500">{basisParts.join(' · ')} · Daily regime context</div>
            {(lineage.entityType === 'AssetIndex' || lineage.entityType === 'mixed') && (
                <p className="mt-2 text-xs text-zinc-400">
                    Index options use their underlying index&apos;s regime. Index share-volume liquidity and company earnings are not
                    applicable; option spread and execution checks still apply.
                </p>
            )}

            <div className="mt-2 flex items-center gap-1.5 text-xs text-zinc-500">
                <FaLock className="h-3 w-3 shrink-0" />
                <span>
                    Training window only — the walk-forward window (→ {lineage.experimentWindow.walkForwardEndDateTime}) was never read.
                </span>
            </div>

            <div className="mt-2 border-t border-zinc-800 pt-1">
                <LineageDisclosure record={record} />
            </div>
        </Card>
    );
}
